import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Eye, Trash2, TrendingDown, TrendingUp, Minus, Sparkles, CheckCircle, ArrowRightLeft, LogIn, RefreshCw, Bell, ShoppingCart, Package, Clock } from "lucide-react";
import { useNavigate } from "react-router-dom";
import {
  getWatchlistItems, removeFromWatchlist,
  getActivitySummary, type WatchlistItem,
} from "@/lib/watchlistStore";
import { usePlan } from "@/hooks/usePlan";
import { useAuth } from "@/contexts/AuthContext";
import { getCloudWatchlist, removeFromCloudWatchlist, getAlertsForWatchlist, markAlertSeen, recheckWatchlistPrices } from "@/lib/cloudServices";

function formatTimeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

interface CloudWatchlistItem extends WatchlistItem {
  lastCheckedAt?: string;
  priceHistory?: { price: number; ts: string; verified?: boolean }[];
  availability?: string;
  bestPriceSeen?: number;
  lastDeliveryTime?: string;
}

interface PriceAlert {
  id: string;
  watchlist_item_id: string | null;
  message: string | null;
  alert_type?: string;
  seen_at: string | null;
  created_at: string;
}

type TimingSignal = {
  text: string;
  className: string;
  icon: typeof CheckCircle;
};

function getTimingSignal(item: CloudWatchlistItem): TimingSignal {
  const history = item.priceHistory ?? [];
  if (history.length >= 2) {
    const recent = history.slice(-3);
    const trending = recent.every((h, i) => i === 0 || h.price <= recent[i - 1].price);
    const trendingUp = recent.every((h, i) => i === 0 || h.price >= recent[i - 1].price);
    
    if (item.bestPriceSeen && item.priceNumeric <= item.bestPriceSeen) {
      return { text: "Price at lowest seen", className: "text-score-high border-score-high/20 bg-score-high/10", icon: CheckCircle };
    }
    if (trending && item.availability === "in_stock") {
      return { text: "Good time to buy", className: "text-score-high border-score-high/20 bg-score-high/10", icon: ShoppingCart };
    }
    if (trendingUp) {
      return { text: "Wait — price trending up", className: "text-score-mid border-score-mid/20 bg-score-mid/10", icon: Clock };
    }
  }
  if (item.availability === "in_stock") {
    return { text: "Stable", className: "text-muted-foreground border-border/30 bg-muted/10", icon: CheckCircle };
  }
  return { text: "Watching", className: "text-muted-foreground border-border/30 bg-muted/10", icon: Eye };
}

function getAvailabilityBadge(availability?: string) {
  switch (availability) {
    case "in_stock": return { text: "In stock", className: "text-score-high" };
    case "low_stock": return { text: "Low stock", className: "text-score-mid" };
    case "out_of_stock": return { text: "Out of stock", className: "text-score-low" };
    default: return null;
  }
}

// Mini sparkline SVG
function Sparkline({ history }: { history?: { price: number; ts: string }[] }) {
  if (!history || history.length < 2) return null;
  const prices = history.map(h => h.price);
  const min = Math.min(...prices);
  const max = Math.max(...prices);
  const range = max - min || 1;
  const w = 48;
  const h = 16;
  const points = prices.map((p, i) => {
    const x = (i / (prices.length - 1)) * w;
    const y = h - ((p - min) / range) * (h - 2) - 1;
    return `${x},${y}`;
  }).join(" ");
  const lastPrice = prices[prices.length - 1];
  const firstPrice = prices[0];
  const color = lastPrice <= firstPrice ? "var(--score-high, #22c55e)" : "var(--score-low, #ef4444)";
  
  return (
    <svg width={w} height={h} className="shrink-0">
      <polyline points={points} fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function cloudToLocal(item: Record<string, unknown>): CloudWatchlistItem {
  return {
    id: item.id as string,
    productName: item.product_name as string,
    query: (item.query as string) ?? "",
    category: (item.category as string) ?? "",
    label: (item.label as string) ?? "",
    price: (item.price as string) ?? "",
    priceNumeric: Number(item.price_numeric) || 0,
    deliveryTime: (item.delivery_time as string) ?? "",
    confidence: (item.confidence as string) ?? "",
    coverage: (item.coverage as string) ?? "",
    type: ((item.watch_type as string) ?? "save") as WatchlistItem["type"],
    addedAt: (item.created_at as string) ?? new Date().toISOString(),
    lastCheckedAt: (item.last_checked_at as string) ?? undefined,
    priceHistory: (item.price_history as any[]) ?? [],
    availability: (item.availability as string) ?? "unknown",
    bestPriceSeen: item.best_price_seen != null ? Number(item.best_price_seen) : undefined,
    lastDeliveryTime: (item.last_delivery_time as string) ?? undefined,
  };
}

type ItemGroup = "action" | "watching" | "stable";

function groupItem(item: CloudWatchlistItem, alert?: PriceAlert): ItemGroup {
  if (alert) return "action";
  const signal = getTimingSignal(item);
  if (signal.text === "Good time to buy" || signal.text === "Price at lowest seen") return "action";
  if (signal.text === "Wait — price trending up") return "watching";
  return "stable";
}

const groupLabels: Record<ItemGroup, string> = {
  action: "Action needed",
  watching: "Watching",
  stable: "Stable",
};

export default function WatchlistPage() {
  const [items, setItems] = useState<CloudWatchlistItem[]>([]);
  const [alerts, setAlerts] = useState<PriceAlert[]>([]);
  const [loading, setLoading] = useState(true);
  const { plan } = usePlan();
  const navigate = useNavigate();
  const { user, isGuest } = useAuth();

  useEffect(() => {
    async function load() {
      if (user) {
        recheckWatchlistPrices().catch(() => {});
        const [{ data }, alertData] = await Promise.all([
          getCloudWatchlist(user.id),
          getAlertsForWatchlist(user.id),
        ]);
        setItems(data.map(cloudToLocal));
        setAlerts(alertData as PriceAlert[]);
      } else {
        setItems(getWatchlistItems() as CloudWatchlistItem[]);
      }
      setLoading(false);
    }
    load();
  }, [user]);

  const handleRemove = async (id: string) => {
    if (user) {
      await removeFromCloudWatchlist(id);
      const { data } = await getCloudWatchlist(user.id);
      setItems(data.map(cloudToLocal));
    } else {
      removeFromWatchlist(id);
      setItems(getWatchlistItems() as CloudWatchlistItem[]);
    }
  };

  const getAlertForItem = (itemId: string) => {
    return alerts.find((a) => a.watchlist_item_id === itemId && !a.seen_at);
  };

  const handleDismissAlert = async (alertId: string) => {
    await markAlertSeen(alertId);
    setAlerts((prev) => prev.map((a) => a.id === alertId ? { ...a, seen_at: new Date().toISOString() } : a));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const unseenAlerts = alerts.filter((a) => !a.seen_at);

  // Group items
  const grouped: Record<ItemGroup, CloudWatchlistItem[]> = { action: [], watching: [], stable: [] };
  items.forEach((item) => {
    const alert = getAlertForItem(item.id);
    const group = groupItem(item, alert);
    grouped[group].push(item);
  });
  const orderedGroups: ItemGroup[] = ["action", "watching", "stable"];

  return (
    <div className="min-h-screen bg-background pt-safe pb-[100px] md:pb-[72px]">
      <div className="max-w-2xl mx-auto px-5 pt-8">
        <h1 className="text-xl font-display font-bold text-foreground mb-1">Watchlist</h1>
        <p className="text-sm text-muted-foreground font-body mb-5">
          Live intelligence on tracked decisions.
        </p>

        {!user && isGuest && items.length > 0 && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="rounded-xl border border-primary/20 bg-primary/5 p-3.5 mb-4">
            <div className="flex items-center gap-2 mb-1.5">
              <LogIn className="w-3.5 h-3.5 text-primary" />
              <p className="text-xs font-body font-medium text-primary">Guest mode</p>
            </div>
            <p className="text-[11px] text-muted-foreground font-body">
              This data is stored locally. Sign in to enable price tracking.
            </p>
            <button onClick={() => navigate("/auth")} className="text-[11px] text-primary font-body font-medium mt-1.5 hover:underline">
              Create account
            </button>
          </motion.div>
        )}

        {/* Price alerts banner */}
        {unseenAlerts.length > 0 && (
          <motion.div initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }} className="rounded-xl border border-score-high/25 bg-score-high/5 p-4 mb-3">
            <div className="flex items-center gap-2 mb-2">
              <Bell className="w-3.5 h-3.5 text-score-high" />
              <p className="text-[10px] text-score-high font-body font-medium uppercase tracking-wider">
                {unseenAlerts.length} price {unseenAlerts.length === 1 ? "alert" : "alerts"}
              </p>
            </div>
            {unseenAlerts.slice(0, 3).map((alert) => (
              <div key={alert.id} className="flex items-center justify-between py-1">
                <div className="flex items-center gap-2 text-sm font-body text-foreground">
                  <TrendingDown className="w-3 h-3 text-score-high" />
                  <span className="text-xs">{alert.message}</span>
                </div>
                <button onClick={() => handleDismissAlert(alert.id)} className="text-[10px] text-muted-foreground font-body hover:text-foreground">
                  Dismiss
                </button>
              </div>
            ))}
          </motion.div>
        )}

        {items.length === 0 ? (
          <div className="text-center py-20">
            <Eye className="w-6 h-6 text-muted-foreground/40 mx-auto mb-4" />
            <h3 className="text-base font-display font-semibold text-foreground mb-1.5">Track products to catch better deals automatically.</h3>
            <p className="text-muted-foreground font-body text-sm max-w-[260px] mx-auto mb-6">
              Save a decision to monitor price changes.
            </p>
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.97 }}
              onClick={() => navigate("/")}
              className="px-5 py-2.5 rounded-xl bg-primary text-primary-foreground font-body text-sm"
            >
              Start a decision
            </motion.button>
          </div>
        ) : (
          <div className="space-y-1">
            {orderedGroups.map((groupKey) => {
              const groupItems = grouped[groupKey];
              if (groupItems.length === 0) return null;
              return (
                <div key={groupKey}>
                  <p className="text-[10px] text-muted-foreground/50 uppercase tracking-wider py-2 font-body">
                    {groupLabels[groupKey]}
                  </p>
                  <AnimatePresence>
                    {groupItems.map((item, i) => {
                      const timing = getTimingSignal(item);
                      const TimingIcon = timing.icon;
                      const itemAlert = getAlertForItem(item.id);
                      const avail = getAvailabilityBadge(item.availability);

                      return (
                        <motion.div
                          key={item.id}
                          initial={{ opacity: 0, y: 8 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, x: -20 }}
                          transition={{ delay: i * 0.03 }}
                          className={`rounded-xl border bg-card p-3.5 mb-2 ${itemAlert ? "border-score-high/30" : "border-border/50"}`}
                        >
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-1.5">
                              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10px] font-body ${timing.className}`}>
                                <TimingIcon className="w-2.5 h-2.5" />
                                {timing.text}
                              </span>
                              {avail && (
                                <span className={`text-[9px] font-body ${avail.className}`}>
                                  {avail.text}
                                </span>
                              )}
                            </div>
                            <span className="text-[10px] text-muted-foreground font-body capitalize">{item.confidence}</span>
                          </div>

                          {/* Alert inline */}
                          {itemAlert && (
                            <div className="flex items-center gap-2 mb-2 px-2 py-1.5 rounded-lg bg-score-high/10 border border-score-high/20">
                              <TrendingDown className="w-3 h-3 text-score-high shrink-0" />
                              <span className="text-[11px] text-score-high font-body font-medium flex-1">{itemAlert.message}</span>
                              <button onClick={() => handleDismissAlert(itemAlert.id)} className="text-[9px] text-muted-foreground font-body hover:text-foreground">✕</button>
                            </div>
                          )}

                          <div className="flex items-start justify-between gap-3">
                            <div className="flex-1 min-w-0">
                              <h3 className="text-sm font-display font-semibold text-foreground truncate">{item.productName}</h3>
                              <p className="text-[11px] text-muted-foreground font-body mt-0.5 truncate">{item.query}</p>
                              {item.lastCheckedAt && (
                                <p className="text-[9px] text-muted-foreground/50 font-body mt-0.5">
                                  Last checked: {formatTimeAgo(item.lastCheckedAt)}
                                </p>
                              )}
                            </div>
                            <div className="flex flex-col items-end gap-0.5 shrink-0">
                              <div className="flex items-center gap-1.5">
                                <Sparkline history={item.priceHistory} />
                                <span className="text-sm font-body font-semibold text-foreground">{item.price}</span>
                              </div>
                              {item.bestPriceSeen != null && item.bestPriceSeen < item.priceNumeric && (
                                <span className="text-[9px] text-muted-foreground/60 font-body">
                                  Low: ${item.bestPriceSeen.toFixed(2)}
                                </span>
                              )}
                            </div>
                          </div>

                          {item.signal && (
                            <div className="flex items-center gap-1.5 mt-2">
                              <Sparkles className="w-3 h-3 text-accent" />
                              <span className="text-[11px] text-accent font-body">{item.signal}</span>
                            </div>
                          )}

                          <div className="flex items-center gap-1.5 mt-2.5">
                            <button
                              onClick={() => navigate(`/?q=${encodeURIComponent(item.query || item.productName)}`)}
                              className="flex items-center gap-1 px-2 py-1.5 rounded-lg text-[11px] font-body text-muted-foreground hover:text-foreground hover:bg-muted/25 transition-colors"
                            >
                              <RefreshCw className="w-3 h-3" />Rerun
                            </button>
                            <button onClick={() => handleRemove(item.id)} className="flex items-center gap-1 px-2 py-1.5 rounded-lg text-[11px] font-body text-muted-foreground hover:text-score-low transition-colors">
                              <Trash2 className="w-3 h-3" />Remove
                            </button>
                          </div>
                        </motion.div>
                      );
                    })}
                  </AnimatePresence>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
